/**
 * LC Learning API
 *
 * TodayLesson, Session management, Library
 */
import { apiClient, ApiResponse } from './client'
import type {
  QuizSolveResultDescription,
  ToonCard,
  ToonQuizCard,
  QuetureTestQueBoardConfig,
} from '@quebon/shared-learning'
import type { SpecialLectureLockView } from './unitAssessment'

// ============================================================================
// Types: TodayLesson
// ============================================================================

export interface LcComponentStatus {
  configComponentId: string    // V2 UUID — identifies the component in the config
  type: string                 // 'video' | 'quiz' | 'guide'
  label: string | null
  labelEn?: string | null
  sortOrder: number
  enabled: boolean
  skipped: boolean
  status: string               // 'not_started' | 'in_progress' | 'completed'
  options: Record<string, unknown> | null
  hasPauseQuizResponse?: boolean // 정규 학습에서 이 video 컴포넌트의 Pause & Quiz 응답이 존재하는지
  wrongCount?: number          // 도서관 오답만 풀기 배지용 (퀴즈 컴포넌트만, baseline 오답 수)
  /** status=skipped 일 때 사유(learner_choice | content_missing). 생각열기 회차별 정책 전용. */
  skipReason?: WarmupSkipReason | null
  /** 서버가 해석한 현재 회차의 생각열기 정책(required | optional). 프론트가 회차를 추측하지 않는다. */
  warmupPolicy?: WarmupPolicyMode | null
  /** 생각열기를 녹음 없이 건너뛸 수 있는지. */
  skipAllowed?: boolean
}

export type WarmupPolicyMode = 'required' | 'optional'
export type WarmupSkipReason = 'learner_choice' | 'content_missing'
/** Runner → LearnPage 완료 결과. 생략 시 completed 로 취급(기존 호출 호환). */
export interface ComponentCompleteResult {
  status: 'completed' | 'skipped'
  skipReason?: WarmupSkipReason | null
}

/** 오늘 레슨의 교재(체크업북/워크북) 안내 — BE MaterialNoticeView 대응. */
export interface MaterialNotice {
  bookId: number
  materialType: 'CHECKUP_BOOK' | 'WORKBOOK'
  title: string
  volumeLabel: string | null
  message: string | null
  coverImageUrl: string | null
  learnerCoverImageUrl?: string | null
  samplePdfUrl: string | null
  sourceType: 'GENERATED_PREVIEW' | 'EXTERNAL_DESIGN'
  startPage: number | null
  endPage: number | null
  blockQuizStartPage?: number | null
}

export interface OfflineTestLockView {
  kind: 'CHAPTER'
  chapterOrdinal: number
  chapterTitle: string | null
  itemCount: number
}

export type CumulativeSegmentStatus = 'AVAILABLE' | 'IN_PROGRESS' | 'COMPLETED' | 'EXEMPTED'

export interface CumulativeSegmentView {
  status: CumulativeSegmentStatus
  /** Latest Math Head Run id (null for the chapter segment or before the first run). */
  latestRunId: number | null
  roundNo: number | null
  canStartOrResume: boolean
}

export interface CumulativeScopeChapterView {
  /** 커리큘럼 전역 번호. 순서 계산용이고 화면 표기는 chapterNumberInCourse 를 쓴다. */
  chapterOrdinal: number
  /** 과정 안에서 1부터 세는 표시용 번호. */
  chapterNumberInCourse: number | null
  chapterTitle: string | null
  courseTitle: string | null
}

export interface CumulativeAssessmentLockView {
  assessmentId: number
  status: 'ACTIVE' | 'RESOLVED'
  scope: {
    startChapterOrdinal: number
    endChapterOrdinal: number
    /** 시험범위 안내용 챕터 목록. courseTitle 은 과정 코드(`CORE-C`) 원문이라 화면에서 이름으로 바꿔 쓴다. */
    chapters?: CumulativeScopeChapterView[]
  }
  chapterComprehensive: CumulativeSegmentView
  mathHead: CumulativeSegmentView
  /** 두 세그먼트를 다 마쳤지만 교사가 아직 [누적 테스트 완료]를 누르지 않아 다음 레슨이 잠겨 있는 상태. */
  awaitingTeacherResolution?: boolean
}

export interface TodayLessonResponse {
  enrollmentId: number
  lessonId: number
  lessonTitle: string
  lessonSequence?: number | null
  chapterNumber?: number | null
  moduleTitle: string | null
  courseTitle: string | null
  isFirstAttempt: boolean
  components: LcComponentStatus[]
  completionRules: Record<string, unknown> | null
  learningSettings: Record<string, unknown> | null
  /** 출석 세션에서 레슨을 끝냈으나 교사가 다음 수업 진행을 승인하지 않은 상태 */
  awaitingAdvance?: boolean
  /** 지정복습 학습은 끝났고 교사의 리뷰노트 채점 완료를 기다리는 상태 */
  awaitingReviewNoteGrade?: boolean
  /** 교사가 지정한 복습 레슨 — FE는 처음부터 시작(component status 무시) */
  isDesignatedReview?: boolean
  /** 현재 레슨 앞에 배치된 단원평가 특강. 존재하면 일반 레슨보다 우선한다. */
  specialLecture?: SpecialLectureLockView | null
  /** 생성되어 채점 완료 전인 오프라인 챕터/누적 테스트. 존재하면 다음 신규 레슨을 시작할 수 없다. */
  offlineTest?: OfflineTestLockView | null
  /** 두 세그먼트 누적 테스트. ACTIVE이면 다음 신규 레슨을 잠근다. */
  cumulativeAssessment?: CumulativeAssessmentLockView | null
  /** 메인 화면에서 준비할 교재 목록. 체크업북 → 워크북, 종류별 최대 한 권. */
  materialNotices?: MaterialNotice[]
  /** 깨처 교재 펼침 팝업에서 안내할 단일 교재. */
  bookOpenNotice?: MaterialNotice | null
}

// ============================================================================
// Types: Session Start
// ============================================================================

export interface LcStartSessionRequest {
  enrollmentId: number
  lessonId: number
  configComponentId: string    // V2 UUID from LcComponentStatus.configComponentId
  componentType: string        // 'video' | 'quiz' | 'guide'
}

export interface PauseAndGoPoint {
  time: number                 // milliseconds
  quizId: string | null
  type: string
  message: string | null
  isBlockQuiz: boolean
  quiz: Record<string, unknown> | null
}

export interface PauseQuizResponseSummary {
  quizId: string
  pauseTimeMs: number
  isCorrect: boolean | null
  isOfflineCompleted: boolean
  attemptNumber: number
}

export interface LcVideoDataResponse {
  hlsUrl: string | null
  thumbnailUrl: string | null
  duration: number | null
  lastPositionSec: number
  /** @deprecated LC FE 는 이번 스펙 이후 skipPolicy/videoComponentEverCompleted 로 판단. */
  skipAllowed: boolean
  requiredWatchPercent: number
  pauseAndGoPoints: PauseAndGoPoint[] | null
  previousPauseResponses: PauseQuizResponseSummary[] | null
  skipPolicy: 'always' | 'after_first_watch'
  speedPolicy: 'always' | 'after_first_watch'
  videoComponentEverCompleted: boolean
  // Video Phase B — 1회차 Pause 모드
  pauseFirstWatchMode: 'guide' | 'solve'
  // G2 — 영상 자동 재생 (config-v2 VideoOptions.autoPlay)
  autoPlay: boolean
  /** 지정 복습 Pause Quiz 응답을 원 학습과 격리하는 회차 ID. */
  learningAttemptId?: number | null
}

/**
 * 재진입 시 서버가 내려주는 이전 제출 기록.
 * LcLearningSessionService.kt:1287-1296 의 Map 구조와 1:1 대응.
 */
export interface LcPreviousQuizResponseData {
  quizId: string
  isCorrect: boolean | null
  corrects: (boolean | null)[]
  correctAnswers: unknown[]
  submittedResponse: unknown[]
  varInstantiation: Record<string, unknown>
  description?: LcQuizResultDescription | null
}

export interface LcQuizDataResponse {
  quizzes: Record<string, unknown>[]
  totalQuizCount: number
  passingScore: number
  maxAttempts: number
  resumeFromIndex: number
  previousResponses: LcPreviousQuizResponseData[]
  solveMode: 'lesson' | 'test'
  showHint: boolean
  /** G3 — 오답 해설 노출 정책. 'always'(기본=해설 노출) | 'hide_on_incorrect'(오답 시 해설 숨김). */
  answerExplanationPolicy?: 'always' | 'hide_on_incorrect'
  /** G4 — 선택지 텍스트 흐리기 정책. 'show'(기본=정상 노출) | 'dimmed'(선택지 텍스트 opacity 0.05). 백엔드가 1회차 한정으로만 'dimmed' 전달. */
  choiceDisplayPolicy?: 'show' | 'dimmed'
  attemptNumber: number
  canRetry: boolean
  /** 완료 컴포넌트 재진입(리뷰) 여부 */
  isReview?: boolean
  /** 리뷰 중 답 재제출 허용 여부 */
  canResubmit?: boolean
  /**
   * G8 — 1회차 "교재 풀이 → 패드 입력(채점 보류)" 단계 여부. true 면 자동채점 대신
   * submitOfflineQuizInput 으로 제출(isCorrect=null). 강사가 코칭 콘솔에서 채점한다.
   */
  offlineInput?: boolean
}

// ============================================================================
// Types: Guide Data
// ============================================================================

export interface LcExplainQuizData {
  quizId: string
  quizType: string
  title: string | null
  image: string | null
}

export interface LcGuideDataResponse {
  message: string | null
  messageEn?: string | null
  explainQuiz: LcExplainQuizData | null
  audioResponse1: string | null
  audioResponse2: string | null
}

export interface LcBuildupStepTitle {
  step: string
  title: string
}

export interface LcBuildupStep4Item {
  number: number
  title: string | null
  problem: string | null
  options: string | null
  probImageUrls: string[]
}

export type BuildupLearnerGrade = 'O' | 'X' | 'TRIANGLE'
export type BuildupGradedBy = 'TEACHER' | 'SELF'

export interface LcBuildupSelfGradeItem {
  number: number
  title: string | null
  problem: string | null
  options: string | null
  answer: string | null
  desc: string | null
  answerCriteria: string | null
  probImageUrls: string[]
  descImageUrls: string[]
  grade: BuildupLearnerGrade | null
  gradedBy: BuildupGradedBy | null
}

export interface LcBuildupSelfGradeStep {
  step: string
  title: string
  items: LcBuildupSelfGradeItem[]
}

export interface LcBuildupDataResponse {
  stepTitles: LcBuildupStepTitle[]
  step4Items: LcBuildupStep4Item[]
  audioResponse1: string | null
  audioResponse2: string | null
  selfGradingEnabled: boolean
  selfGradeSteps: LcBuildupSelfGradeStep[]
}

// Phase 4c (§5-8=A): 설명하기(빌드업 STEP4) 사진/QR 업로드.
export interface ExplainPhoto {
  id: number
  photoUrl: string
  ordinal: number
  uploadedAt: string
}

export interface ExplainPhotoTokenResponse {
  token: string
  expiresAt: string
}

export interface ExplainPhotoUploadUrlResponse {
  uploadUrl: string
  fileUrl: string
}

export interface LcWarmupData {
  warmupText: string | null
  shortPoint: string | null
  audioEnabled: boolean
  audioRequired: boolean
  /** 현재 회차의 음성. 지정 복습은 회차 격리 음성만 온다(최초 녹음 아님). */
  audioResponseUrl: string | null
  /** 현재 회차 유효 정책. 구버전 백엔드는 미전달 → required 로 취급. */
  policy?: WarmupPolicyMode
  skipAllowed?: boolean
  /** 현재 회차 기록 상태(not_started | in_progress | completed | skipped). 재진입 시 스킵 복원용. */
  componentStatus?: string
  skipReason?: WarmupSkipReason | null
  /** 본문·포인트가 모두 비어 서버가 생략 대상으로 판정·기록했는지. */
  contentMissing?: boolean
}

// ============================================================================
// Types: Toon Data
// ============================================================================

export interface LcToonData {
  qnIntro: ToonCard[] | null
  qnOutro: ToonQuizCard[] | null
}

export interface LcToolData {
  kind: 'queboard'
  configs: QuetureTestQueBoardConfig[]
}

export interface LcStartSessionResponse {
  sessionId: number | null
  componentProgressId: number | null
  isFirstAttempt: boolean
  videoData: LcVideoDataResponse | null
  quizData: LcQuizDataResponse | null
  guideData: LcGuideDataResponse | null
  toonData?: LcToonData | null
  buildupData?: LcBuildupDataResponse | null
  toolData?: LcToolData | null
  warmupData?: LcWarmupData | null
  /** 지정 복습이면 이 세션이 속한 회차 ID. 스킵·음성 저장 문맥으로 되돌려 보낸다. */
  learningAttemptId?: number | null
}

// ============================================================================
// Types: Session Actions
// ============================================================================

export interface LcVideoHeartbeatRequest {
  enrollmentId: number
  currentPositionSec: number
  watchedDurationSec: number
  totalDurationSec: number
  requiredWatchPercent: number
}

export interface LcVideoEventRequest {
  enrollmentId: number
  eventType: 'play' | 'pause' | 'seek' | 'end'
  startPositionSec: number | null
  endPositionSec: number | null
  seekToSec: number | null
  playbackSpeed: number
}

export interface LcQuizResponseRequest {
  quizId: string
  scriptId?: string | null
  answers: unknown[]
  vars?: Record<string, unknown> | null
  timeSpentSec?: number
}

export interface LcPauseQuizResponseRequest {
  enrollmentId: number
  learningAttemptId?: number | null
  quizId: string
  answers: (string | string[] | number | null)[]
  pauseTimeMs: number
  vars?: Record<string, unknown> | null
  /** 지정 복습 서술형에서 학습자 입력 없이 모범답안을 확인하고 정답 처리한다. */
  answerRevealOnly?: boolean
}

export interface LcPauseQuizResponseResult {
  responseId: string
  quizId: string
  isCorrect: boolean | null
  corrects: (boolean | null)[]
  correctAnswers: (string | string[] | number | null)[]
  description: LcQuizResultDescription | null
}

export interface LcPauseQuizOfflineCompleteRequest {
  quizId: string
  pauseTimeMs: number
  /**
   * G8 — 블럭(pause) 오프라인 입력값. 있으면 BE 가 submittedResponse 에 채운다(채점은 강사, isCorrect=null 유지).
   * 누락 시 기존 동작(빈 센티넬) 유지.
   */
  answers?: unknown[]
}

export interface LcPauseQuizOfflineCompleteResult {
  responseId: string
  quizId: string
  isOfflineCompleted: boolean
}

export interface LcCompleteSessionRequest {
  enrollmentId: number
  componentType: string
  passScore?: number           // default 0
  maxAttempts?: number
}

export interface CascadeResult {
  [key: string]: unknown
}

export interface LcCompleteSessionResponse {
  componentProgressId: number
  componentStatus: string
  lessonCompleted: boolean
  nextComponentId: string | null
  cascadeResult: CascadeResult | null
  scorePercent: number | null
  passed: boolean | null
  attemptNumber: number | null
  canRetry: boolean | null
  /**
   * 모든 필수 학습자 컴포넌트는 끝났지만 정규 레슨 완료(content_progress.completed)는 교사
   * '다음 수업 진행'까지 지연된 상태. lessonCompleted || lessonReadyForTeacherAdvance 를
   * "태블릿 학습 끝(교사 진행 대기)"으로 취급한다.
   */
  lessonReadyForTeacherAdvance?: boolean
  /** 지정복습 컴포넌트 완료 후 리뷰노트 채점이 남아 attempt 완료를 보류한 상태. */
  awaitingReviewNoteGrade?: boolean
  /** componentStatus=skipped 로 남은 경우(늦은 complete 는 skipped 유지) 사유. */
  skipReason?: WarmupSkipReason | null
}

// === 선택 생각열기 건너뛰기 ===

export interface LcSkipSessionRequest {
  enrollmentId: number
  /** 지정 복습이면 시작 응답의 learningAttemptId. */
  learningAttemptId?: number | null
}

export interface LcSkipSessionResponse {
  componentProgressId: number
  /** 실제 기록 상태 — skipped 또는(이미 완료된 뒤 늦게 도착) completed. */
  componentStatus: string
  skipReason: WarmupSkipReason | null
  /** ISO-8601, Z/offset 포함. */
  skippedAt: string | null
  learningAttemptId: number | null
  nextComponentId: string | null
  /** 학습자 스킵으로는 true 가 되지 않는다. */
  lessonCompleted: boolean
  lessonReadyForTeacherAdvance?: boolean
  awaitingReviewNoteGrade?: boolean
}

// ============================================================================
// Types: Library
// ============================================================================

export interface LibraryLessonNode {
  contentId: number
  title: string
  thumbnailUrl: string | null
  status: string              // 'not_started' | 'in_progress' | 'completed'
  completedAt: string | null
}

export interface LibraryModuleNode {
  contentId: number
  title: string
  lessons: LibraryLessonNode[]
}

export interface LibraryCourseNode {
  contentId: number
  title: string
  modules: LibraryModuleNode[]
}

export interface LibraryCurriculumNode {
  contentId: number
  title: string
  courses: LibraryCourseNode[]
}

export interface LibraryResponse {
  enrollmentId: number
  productName: string
  curriculum: LibraryCurriculumNode
}

export interface LibraryLessonDetailResponse {
  enrollmentId: number
  lessonId: number
  lessonTitle: string
  components: LcComponentStatus[]
  lessonSequence?: number | null
  chapterNumber?: number | null
  moduleTitle?: string | null
  courseTitle?: string | null
  thumbnailUrl?: string | null
}

// ============================================================================
// Types: Today Unit (FE-only polymorphic adapter)
// ============================================================================
// BE 는 현재 lesson 모양만 반환한다(TodayLessonResponse). FE 는 "오늘의
// 학습 단위" 가 lesson/review/moduleTest 세 가지가 될 수 있음을 UI 모델로
// 반영하기 위해, API 응답을 TodayUnit discriminated union 으로 감싼다.
// 향후 BE 가 { kind: 'moduleTest', ... } 를 반환하기 시작하면 adaptTodayUnit
// 에서 분기를 추가한다.
// Spec: docs/superpowers/specs/2026-04-20-lc-learner-config-v2-ui-design.md §3.2
// ============================================================================

export type TodayUnit = LessonUnit | ModuleTestUnit

export interface LessonUnit {
  kind: 'lesson'
  mode: 'progress' | 'review'         // FE 파생: isFirstAttempt 에서 산출
  enrollmentId: number
  lessonId: number
  lessonTitle: string
  moduleTitle: string | null
  courseTitle: string | null
  isFirstAttempt: boolean
  components: LcComponentStatus[]
  completionRules: Record<string, unknown> | null
  learningSettings: Record<string, unknown> | null
}

export interface ModuleTestUnit {
  kind: 'moduleTest'
  // TBD — 이번 스펙에서는 타입 자리만 예약. 내부 속성 없음.
}

export function adaptTodayUnit(r: TodayLessonResponse): LessonUnit {
  return {
    kind: 'lesson',
    mode: r.isFirstAttempt ? 'progress' : 'review',
    enrollmentId: r.enrollmentId,
    lessonId: r.lessonId,
    lessonTitle: r.lessonTitle,
    moduleTitle: r.moduleTitle,
    courseTitle: r.courseTitle,
    isFirstAttempt: r.isFirstAttempt,
    components: r.components,
    completionRules: r.completionRules,
    learningSettings: r.learningSettings,
  }
}

// ============================================================================
// API Functions
// ============================================================================

export async function getTodayLesson(): Promise<TodayLessonResponse> {
  const res = await apiClient.get<ApiResponse<TodayLessonResponse>>('/lc/my/today-lesson')
  return res.data.data
}

export async function startSession(request: LcStartSessionRequest): Promise<LcStartSessionResponse> {
  const res = await apiClient.post<ApiResponse<LcStartSessionResponse>>(
    '/lc/learning/sessions/start',
    request
  )
  return res.data.data
}

export async function sendVideoHeartbeat(
  componentProgressId: number,
  data: LcVideoHeartbeatRequest
): Promise<void> {
  await apiClient.post<ApiResponse<void>>(
    `/lc/learning/sessions/${componentProgressId}/video-heartbeat`,
    data
  )
}

export async function sendVideoEvent(
  componentProgressId: number,
  data: LcVideoEventRequest
): Promise<void> {
  await apiClient.post<ApiResponse<void>>(
    `/lc/learning/sessions/${componentProgressId}/video-event`,
    data
  )
}

export interface LcQuizResultDescription extends QuizSolveResultDescription {
  videoUrl?: string | null
}

export interface LcQuizResponseResult {
  responseId: string
  quizId: string
  isCorrect: boolean | null
  corrects: (boolean | null)[]
  correctAnswers: unknown[]
  description: LcQuizResultDescription | null
  partialCredit: number | null
}

export async function submitQuizResponse(
  sessionId: number,
  data: LcQuizResponseRequest
): Promise<LcQuizResponseResult> {
  const res = await apiClient.post<ApiResponse<LcQuizResponseResult>>(
    `/lc/learning/sessions/${sessionId}/quiz-response`,
    data
  )
  return res.data.data
}

/**
 * G8 — standalone 퀴즈 1회차 오프라인 입력 제출(제출만, 채점 보류).
 * 자동채점 없이 submittedResponse 만 저장(isCorrect=null). 강사가 코칭 콘솔에서 채점한다.
 */
export async function submitOfflineQuizInput(
  sessionId: number,
  data: LcQuizResponseRequest,
): Promise<{ responseId: string; quizId: string; isOfflineCompleted: boolean }> {
  const res = await apiClient.post<
    ApiResponse<{ responseId: string; quizId: string; isOfflineCompleted: boolean }>
  >(`/lc/learning/sessions/${sessionId}/quiz-offline-input`, data)
  return res.data.data
}

export async function submitPauseQuizResponse(
  componentProgressId: number,
  data: LcPauseQuizResponseRequest
): Promise<LcPauseQuizResponseResult> {
  const res = await apiClient.post<ApiResponse<LcPauseQuizResponseResult>>(
    `/lc/learning/sessions/${componentProgressId}/pause-quiz-response`,
    data
  )
  return res.data.data
}

export async function submitPauseQuizOfflineComplete(
  componentProgressId: number,
  data: LcPauseQuizOfflineCompleteRequest
): Promise<LcPauseQuizOfflineCompleteResult> {
  const res = await apiClient.post<ApiResponse<LcPauseQuizOfflineCompleteResult>>(
    `/lc/learning/sessions/${componentProgressId}/pause-quiz-offline-complete`,
    data
  )
  return res.data.data
}

export async function completeSession(
  componentProgressId: number,
  data: LcCompleteSessionRequest
): Promise<LcCompleteSessionResponse> {
  const res = await apiClient.post<ApiResponse<LcCompleteSessionResponse>>(
    `/lc/learning/sessions/${componentProgressId}/complete`,
    data
  )
  return res.data.data
}

/** 선택(optional) 생각열기 건너뛰기. 사유는 서버가 결정한다. */
export async function skipSession(
  componentProgressId: number,
  data: LcSkipSessionRequest,
): Promise<LcSkipSessionResponse> {
  const res = await apiClient.post<ApiResponse<LcSkipSessionResponse>>(
    `/lc/learning/sessions/${componentProgressId}/skip`,
    data,
  )
  return res.data.data
}

export async function getLibrary(): Promise<LibraryResponse> {
  const res = await apiClient.get<ApiResponse<LibraryResponse>>('/lc/my/library')
  return res.data.data
}

export async function getLibraryLessonComponents(
  lessonId: number
): Promise<LibraryLessonDetailResponse> {
  const res = await apiClient.get<ApiResponse<LibraryLessonDetailResponse>>(
    `/lc/my/library/lessons/${lessonId}/components`
  )
  return res.data.data
}

// ============================================================================
// Guide Audio APIs
// ============================================================================

export interface LcAudioUploadUrlResponse {
  uploadUrl: string
  fileUrl: string
}

export async function getAudioUploadUrl(
  componentProgressId: number,
  slot: 1 | 2,
  learningAttemptId?: number | null,
): Promise<LcAudioUploadUrlResponse> {
  const res = await apiClient.post<ApiResponse<LcAudioUploadUrlResponse>>(
    `/lc/learning/sessions/${componentProgressId}/audio-upload-url`,
    learningAttemptId != null ? { slot, learningAttemptId } : { slot }
  )
  return res.data.data
}

export async function saveAudioResponse(
  componentProgressId: number,
  slot: 1 | 2,
  audioUrl: string
): Promise<void> {
  await apiClient.post(
    `/lc/learning/sessions/${componentProgressId}/audio-response`,
    { slot, audioUrl }
  )
}

export async function saveBuildupAudioResponse(
  componentProgressId: number,
  slot: 1 | 2,
  audioUrl: string,
  learningAttemptId?: number | null,
): Promise<void> {
  await apiClient.post(
    `/lc/learning/sessions/${componentProgressId}/buildup-audio-response`,
    learningAttemptId != null ? { slot, audioUrl, learningAttemptId } : { slot, audioUrl },
  )
}

export interface SelfGradeBuildupStepRequest {
  step: string
  itemNo: number
  grade: BuildupLearnerGrade
}

export async function selfGradeBuildupStep(
  componentProgressId: number,
  body: SelfGradeBuildupStepRequest,
): Promise<void> {
  await apiClient.post(
    `/lc/learning/sessions/${componentProgressId}/buildup-self-grade`,
    body,
  )
}

export async function uploadAudioToS3(uploadUrl: string, blob: Blob): Promise<void> {
  await fetch(uploadUrl, {
    method: 'PUT',
    body: blob,
    headers: { 'Content-Type': 'audio/webm' },
  })
}

// ============================================================================
// 설명하기(빌드업 STEP4) 사진/QR 업로드 (Phase 4c, §5-8=A)
// ============================================================================

/** 학습자(로그인): cp 의 QR 업로드 토큰 발급/재사용. */
export async function issueExplainPhotoToken(
  componentProgressId: number,
): Promise<ExplainPhotoTokenResponse> {
  const res = await apiClient.post<ApiResponse<ExplainPhotoTokenResponse>>(
    `/lc/learning/sessions/${componentProgressId}/explain-photo-token`,
  )
  return res.data.data
}

/** 학습자(로그인): 업로드 사진 목록(폴링). */
export async function listExplainPhotos(componentProgressId: number): Promise<ExplainPhoto[]> {
  const res = await apiClient.get<ApiResponse<ExplainPhoto[]>>(
    `/lc/learning/sessions/${componentProgressId}/explain-photos`,
  )
  return res.data.data
}

/** 비로그인 모바일: 토큰으로 presigned 업로드 URL 요청. */
export async function getExplainPhotoUploadUrl(
  token: string,
  contentType: string,
): Promise<ExplainPhotoUploadUrlResponse> {
  const res = await apiClient.post<ApiResponse<ExplainPhotoUploadUrlResponse>>(
    '/lc/learning/explain-photo/upload-url',
    { token, contentType },
  )
  return res.data.data
}

/** 비로그인 모바일: S3 PUT 완료 후 사진 행 저장 확정. */
export async function confirmExplainPhoto(token: string, fileUrl: string): Promise<ExplainPhoto> {
  const res = await apiClient.post<ApiResponse<ExplainPhoto>>(
    '/lc/learning/explain-photo/confirm',
    { token, fileUrl },
  )
  return res.data.data
}

/** 비로그인 모바일: 업로드 사진 목록(토큰). */
export async function listExplainPhotosByToken(token: string): Promise<ExplainPhoto[]> {
  const res = await apiClient.get<ApiResponse<ExplainPhoto[]>>(
    '/lc/learning/explain-photo/list',
    { params: { token } },
  )
  return res.data.data
}

/** 비로그인 모바일: 토큰에 바인딩된 업로드 사진 삭제. */
export async function deleteExplainPhotoByToken(token: string, photoId: number): Promise<void> {
  await apiClient.delete<ApiResponse<{ deleted: boolean }>>(
    `/lc/learning/explain-photo/${photoId}`,
    { params: { token } },
  )
}

/**
 * 이미지 파일을 presigned URL 로 직접 PUT.
 * contentType 은 presigned 발급에 쓴 값과 **반드시 동일**해야 한다(S3 SigV4 가 Content-Type 을 서명에 포함 →
 * 불일치 시 403 SignatureDoesNotMatch). 빈 file.type 폴백을 발급·PUT 양쪽에 일관 적용하기 위해 인자로 받는다.
 * res.ok 검사로 403 등 실패를 삼키지 않고 throw 한다(무성 실패 → 깨진 사진 행 저장 방지).
 */
export async function uploadImageToS3(
  uploadUrl: string,
  file: File,
  contentType: string,
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': contentType },
  })
  if (!res.ok) {
    throw new Error(`S3 upload failed: ${res.status}`)
  }
}
